'use strict'

// Session length and inactivity for people who sign in to a live app.
// Minutes are whole numbers. Null inactivity means the sign-in lasts until
// the session length, which is how apps behaved before this setting existed.

const DEFAULT_SESSION_MINUTES = 30 * 24 * 60
const DEFAULT_REMEMBER_MINUTES = 14 * 24 * 60
const MIN_SESSION_MINUTES = 1
const MAX_SESSION_MINUTES = 30 * 24 * 60
const MIN_INACTIVITY_MINUTES = 1
const MAX_INACTIVITY_MINUTES = 30 * 24 * 60
const SESSION_TOUCH_GAP_MS = 15 * 1000
const SESSION_WARNING_LEAD_MS = 60 * 1000
// Browsers and Node treat a longer delay as 1ms, which would sign the tab out at once.
const MAX_TIMER_MS = 2147483647

const UNIT_MINUTES = { minutes: 1, hours: 60, days: 1440 }

function defaultUserSession () {

  return {
    sessionMinutes: DEFAULT_SESSION_MINUTES,
    inactivityMinutes: null,
    rememberMeMinutes: null
  }
}

function rememberFrom (input, session) {

  if (!input || !Object.prototype.hasOwnProperty.call(input, 'rememberMeMinutes')) return null

  const raw = input.rememberMeMinutes

  if (raw == null || raw === '' || raw === 0 || raw === '0') return null

  const minutes = asWholeNumber(raw)

  if (minutes == null) return { error: 'Remember me must be a whole number of minutes' }

  if (minutes < MIN_SESSION_MINUTES || minutes > MAX_SESSION_MINUTES) {

    return { error: `Remember me must be between ${MIN_SESSION_MINUTES} and ${MAX_SESSION_MINUTES} minutes` }
  }

  if (minutes < session) return { error: 'Remember me must be at least as long as the session' }

  return minutes
}

function withRemember (session, inactivity, input) {

  const remember = rememberFrom(input, session)

  if (remember && typeof remember === 'object') return remember

  return {
    sessionMinutes: session,
    inactivityMinutes: inactivity,
    rememberMeMinutes: remember
  }
}

function asWholeNumber (value) {

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) value = Number(value.trim())
  if (typeof value !== 'number' || !Number.isInteger(value)) return null

  return value
}

function normalizeUserSession (input) {

  if (input == null) return defaultUserSession()

  if (typeof input !== 'object' || Array.isArray(input)) {

    return { error: 'User session settings must be an object' }
  }

  const session = asWholeNumber(input.sessionMinutes)

  if (session == null) return { error: 'Session length must be a whole number of minutes' }

  if (session < MIN_SESSION_MINUTES || session > MAX_SESSION_MINUTES) {

    return { error: `Session length must be between ${MIN_SESSION_MINUTES} and ${MAX_SESSION_MINUTES} minutes` }
  }

  const rawIdle = input.inactivityMinutes

  if (rawIdle == null || rawIdle === '' || rawIdle === 0 || rawIdle === '0') {

    return withRemember(session, null, input)
  }

  const idle = asWholeNumber(rawIdle)

  if (idle == null) return { error: 'Inactivity timeout must be a whole number of minutes' }

  if (idle < MIN_INACTIVITY_MINUTES || idle > MAX_INACTIVITY_MINUTES) {

    return { error: `Inactivity timeout must be between ${MIN_INACTIVITY_MINUTES} and ${MAX_INACTIVITY_MINUTES} minutes` }
  }

  if (idle > session) return { error: 'Inactivity timeout must be shorter than or equal to the session length' }

  return withRemember(session, idle, input)
}

function sessionFields (normalized) {

  return {
    sessionMinutes: normalized.sessionMinutes,
    inactivityMinutes: normalized.inactivityMinutes,
    rememberMeMinutes: normalized.rememberMeMinutes
  }
}

function userSessionForRead (input) {

  const normalized = normalizeUserSession(input)

  if (!normalized.error) return sessionFields(normalized)

  if (input && typeof input === 'object' && !Array.isArray(input)) {

    const relaxed = normalizeUserSession(Object.assign({}, input, { rememberMeMinutes: null }))

    if (!relaxed.error) return sessionFields(relaxed)
  }

  return defaultUserSession()
}

function displayedRememberMinutes (policy) {

  const session = userSessionForRead(policy)

  if (session.rememberMeMinutes != null) return session.rememberMeMinutes

  return Math.max(session.sessionMinutes, Math.min(DEFAULT_REMEMBER_MINUTES, MAX_SESSION_MINUTES))
}

function grantedSessionMinutes (policy, rememberMe) {

  const session = userSessionForRead(policy)

  if (!rememberMe || session.inactivityMinutes) return session.sessionMinutes

  return displayedRememberMinutes(session)
}

function effectiveSessionMinutes (policy, row) {

  const session = userSessionForRead(policy)

  if (session.inactivityMinutes) return session.sessionMinutes
  if (row && row.rememberMe && row.grantedMinutes) return row.grantedMinutes

  return session.sessionMinutes
}

function sessionWarningMessage () {

  return 'You will be signed out in one minute because of inactivity.'
}

function sessionStatus ({ issuedAt, lastActivityAt, now, policy }) {

  const sessionMs = policy.sessionMinutes * 60000
  const expiresAt = issuedAt + sessionMs

  if (now >= expiresAt) return { ok: false, reason: 'expired', expiresAt }

  let idleExpiresAt = null

  if (policy.inactivityMinutes) {

    idleExpiresAt = lastActivityAt + policy.inactivityMinutes * 60000

    if (now >= idleExpiresAt) return { ok: false, reason: 'idle', expiresAt, idleExpiresAt }
  }

  return { ok: true, expiresAt, idleExpiresAt }
}

function sessionEndedMessage (reason) {

  if (reason === 'idle') return 'You were signed out after a period of inactivity.'
  if (reason === 'expired' || reason === 'revoked') return 'Your session has ended. Sign in again.'

  return ''
}

function minutesToParts (minutes) {

  const units = [['days', UNIT_MINUTES.days], ['hours', UNIT_MINUTES.hours], ['minutes', UNIT_MINUTES.minutes]]

  for (const [unit, size] of units) {

    if (minutes >= size && minutes % size === 0) return { count: minutes / size, unit }
  }

  return { count: minutes, unit: 'minutes' }
}

function partsToMinutes (count, unit) {

  if (!Object.prototype.hasOwnProperty.call(UNIT_MINUTES, unit)) {

    return { error: 'Choose minutes, hours, or days' }
  }

  const parsed = asWholeNumber(count)

  if (parsed == null || parsed < 1) return { error: 'Enter a whole number of 1 or more' }

  return { minutes: parsed * UNIT_MINUTES[unit] }
}

function readTokenPayload (token) {

  if (!token || typeof token !== 'string') return null

  const body = token.split('.')[0]

  if (!body) return null

  try {

    const pad = body.replace(/-/g, '+').replace(/_/g, '/')
    const padded = pad + '='.repeat((4 - pad.length % 4) % 4)
    const json = typeof Buffer !== 'undefined'
      ? Buffer.from(padded, 'base64').toString('utf8')
      : decodeBase64Url(padded)

    const payload = JSON.parse(json)

    if (!payload || typeof payload !== 'object') return null

    return payload
  } catch (error) {

    return null
  }
}

function decodeBase64Url (padded) {

  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)

  return new TextDecoder().decode(bytes)
}

// Local timers for the open tab. The server still decides whether the token works.
function createSessionWatch (options) {

  const now = options.now == null ? Date.now() : options.now
  let idleTimer = null
  let absoluteTimer = null
  let warningTimer = null
  let lastTouch = 0
  let stopped = false
  const idleMs = options.inactivityMinutes ? options.inactivityMinutes * 60000 : null
  const expired = options.expiresAt != null && options.expiresAt <= now
  const warningLeadMs = options.warningLeadMs == null
    ? (idleMs ? SESSION_WARNING_LEAD_MS : 0)
    : options.warningLeadMs

  function stop () {

    stopped = true
    if (idleTimer) clearTimeout(idleTimer)
    if (absoluteTimer) clearTimeout(absoluteTimer)
    if (warningTimer) clearTimeout(warningTimer)
    idleTimer = null
    absoluteTimer = null
    warningTimer = null
  }

  function arm (delay, run, slot) {

    if (stopped || delay <= 0) {

      if (slot === 'idle') idleTimer = null
      else if (slot === 'warning') warningTimer = null
      else absoluteTimer = null
      if (!stopped && delay <= 0) run()

      return null
    }

    const wait = Math.min(delay, MAX_TIMER_MS)
    const timer = setTimeout(() => arm(delay - wait, run, slot), wait)

    if (slot === 'idle') idleTimer = timer
    else if (slot === 'warning') warningTimer = timer
    else absoluteTimer = timer

    return timer
  }

  function scheduleWarning (from) {

    if (warningTimer) clearTimeout(warningTimer)
    warningTimer = null
    if (stopped || !idleMs || !warningLeadMs || !options.onWarning) return

    const idleEnd = from + idleMs
    const end = options.expiresAt == null ? idleEnd : Math.min(idleEnd, options.expiresAt)

    arm(end - from - warningLeadMs, () => {

      if (!stopped && options.onWarning) options.onWarning()
    }, 'warning')
  }

  function noteActivity (at) {

    if (stopped || expired || !idleMs) return

    const when = at == null ? Date.now() : at

    if (idleTimer) clearTimeout(idleTimer)

    arm(idleMs, () => {

      if (!stopped && options.onIdle) options.onIdle()
    }, 'idle')

    scheduleWarning(when)

    if (!options.onTouch) return
    if (lastTouch && when - lastTouch < SESSION_TOUCH_GAP_MS) return

    lastTouch = when
    options.onTouch()
  }

  if (!expired && options.expiresAt != null) {

    arm(options.expiresAt - now, () => {

      if (!stopped && options.onExpired) options.onExpired()
    }, 'absolute')
  }

  if (!expired) noteActivity(now)

  return { stop, noteActivity, expired }
}

module.exports = {
  DEFAULT_SESSION_MINUTES,
  DEFAULT_REMEMBER_MINUTES,
  MIN_SESSION_MINUTES,
  MAX_SESSION_MINUTES,
  MIN_INACTIVITY_MINUTES,
  MAX_INACTIVITY_MINUTES,
  SESSION_TOUCH_GAP_MS,
  SESSION_WARNING_LEAD_MS,
  MAX_TIMER_MS,
  defaultUserSession,
  normalizeUserSession,
  userSessionForRead,
  displayedRememberMinutes,
  grantedSessionMinutes,
  effectiveSessionMinutes,
  sessionStatus,
  sessionEndedMessage,
  sessionWarningMessage,
  minutesToParts,
  partsToMinutes,
  readTokenPayload,
  createSessionWatch
}
